// 他参加者がテキスト / 付箋を編集中のキャレット位置を表示するレイヤ（エピックボード相当）。
//
// transient store（useCanvasTransientStore）の textCarets を購読し、各キャレットについて対象オブジェクトの
// エディタ DOM（data-testid="canvas-object-text-<id>"）から cursorIndex 文字目の位置を Range で実測して
// ステージ座標へ変換し、色付きの縦バー＋名前ラベルで描画する。自分自身のキャレットは描画しない。
// 範囲選択中（selectionEndIndex あり）は、あわせて選択範囲を選択者の色の半透明の帯で塗る。
//
// 制約:
// - 回転しているオブジェクトはキャレット位置計算が不正確になるため描画しない（best-effort）。
// - 送信側は生 Markdown テキスト基準（改行込み）の文字オフセットを送る。受信側は表示 DOM が
//   Markdown 整形されている（マーカーが隠れる / グリフが付く）ため、生オフセットをそのまま
//   テキストノードへ当てると見出し・箇条書きでずれる。これを避けるため mapRawOffsetToDisplay で
//   「行 index + 行内オフセット」へ写像し、対象行の要素内だけを走査して実測する。
//   行要素は子要素の index ではなく `[data-md-line="<i>"]` で引く。非編集表示の表は 1 行が
//   複数のセル要素（`[data-md-cell]`）へ分かれるため、写像が返す cellIndex も併せて引く。
//   受信側 DOM が編集中（WYSIWYG）か非編集（MarkdownTextView）かで写像規則が異なるため、
//   [data-md] ブロックの有無で surface を判定する。

import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  tableStructureRevisionOf,
  type CanvasObject,
  type TableCanvasObject,
  type Viewer,
} from "@refinement-canvas/domain";
import { tableObjectCellDisplayOffset } from "../../lib/markdown-text";
import { findCanvasObjectElement } from "./canvas-object-element";
import { observeObjectLayout } from "./object-layout-observer";
import {
  clampCaretLeftToLineContent,
  clampCaretToBox,
  clipRectToBox,
  REMOTE_CARET_BAR_SCREEN_PX,
  resolveClampBox,
  resolveStageZoom,
  toObjectLocalRect,
  type ClampBox,
  type ObjectLocalRect,
  type ScreenRect,
} from "./remote-caret-geometry";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";
import {
  useCanvasTransientStore,
  type RemoteTextCaret,
} from "./store/canvas-transient-store";
import { screenToStage, type ViewState } from "../../lib/canvas-coordinates";
import {
  mapRawOffsetToDisplay,
  resolveCaretTextAnchor,
  type CaretSurface,
  type DisplayCaretPosition,
} from "../../lib/markdown-wysiwyg";
import {
  REMOTE_TRANSIENT_PRUNE_INTERVAL_MS,
  REMOTE_TRANSIENT_STALE_MS,
} from "./remote-transient-timing";

interface Props {
  readonly currentUserId: string;
}

const CARET_LAYER_Z_INDEX = Number.MAX_SAFE_INTEGER - 3;

// 一度に描く選択帯の上限。本文上限（8192 文字）を全選択されると行数ぶんの矩形が返るため、
// DOM 要素数が跳ねないように打ち切る。打ち切っても「広い範囲が選ばれている」ことは伝わる
// （キャレットの縦バーは範囲の先頭にあるので、どこから選ばれているかは分かる）。
// 行数を主の上限とし、矩形数は「1 行が極端に折り返す」場合の絶対上限として置く。
const REMOTE_SELECTION_MAX_LINES = 200;
const REMOTE_SELECTION_MAX_RECTS = 400;
/**
 * 1 行から取り込む「重複除去前」の矩形数の上限。
 * 重複除去（dropContainedRects）は O(n^2) のため、極端に折り返した 1 行で計算量が跳ねないよう
 * ここで頭打ちにする。インライン要素の box が行ボックスと重なって返るぶん（おおむね 2 倍）を
 * 見込んで描画上限の 2 倍にしてあり、重複で描画枠を食い潰さない。
 */
const REMOTE_SELECTION_MAX_LINE_RECTS = REMOTE_SELECTION_MAX_RECTS * 2;
/** 空行（<br> だけの行）に置く帯の幅（画面 px）。選択が途切れて見えないようにする。 */
const EMPTY_LINE_BAND_WIDTH_PX = 4;
/** 名前ラベルの下端と縦バーの上端の隙間（画面 px）。逆スケールの transform の中で使うため、拡大率に依らない。 */
const REMOTE_CARET_LABEL_GAP_PX = 2;

/** 選択帯 1 枚分の矩形（ステージ座標）。 */
interface SelectionRect {
  readonly stageX: number;
  readonly stageY: number;
  readonly stageWidth: number;
  readonly stageHeight: number;
}

interface CaretPosition {
  readonly stageX: number;
  readonly stageY: number;
  /** キャレットの高さ（ステージ座標 px = 行高）。 */
  readonly stageHeight: number;
  /**
   * 縦バーの描画幅の上限（ステージ座標 px）。枠の右辺までの残り幅で、
   * store の倍率（--canvas-inv-zoom）と DOM 実測の倍率が食い違っても右辺を超えさせないために使う。
   * 表のセル（枠へ切り詰めない経路）では undefined で、従来どおり幅の上限を設けない。
   */
  readonly barMaxWidth?: number | undefined;
  readonly color: string;
  readonly displayName: string;
  /** 範囲選択中の帯（行ボックスごと）。選択が無ければ空配列。 */
  readonly selectionRects: readonly SelectionRect[];
}

/**
 * 画面座標を「図形本体を原点とするステージ座標」へ変換する変換器と、切り詰め先の枠。
 * 本体要素が取れるときは本体矩形と DOM 実測の倍率、取れないときは screenToStage と
 * store の object.x / y を使う。どちらの場合も、以後の切り詰め・帯の算出・描画は同じ手順で行う。
 */
interface ObjectAnchor {
  readonly zoom: number;
  readonly box: ClampBox;
  toLocal(rect: ScreenRect): ObjectLocalRect;
}

export function CanvasRemoteCaretsLayer({ currentUserId }: Props): JSX.Element {
  const textCarets = useCanvasTransientStore((s) => s.textCarets);
  const prune = useCanvasTransientStore((s) => s.prune);
  const objects = useCanvasDocStore((s) => s.objects);
  const viewers = useCanvasViewersStore((s) => s.viewers);
  const [positions, setPositions] = useState<Record<string, CaretPosition>>({});
  // 表の中のスクロールは objects / textCarets を変えないため、再計算の契機を別に持つ。
  // 受信側自身のスクロール・編集表示の切替・編集終了（scroll=0 へ戻す）でも座標が変わる。
  const [tableScrollSignal, setTableScrollSignal] = useState(0);
  // 図形本体の大きさの変化とフォント読み込み完了（いずれも store を変えない）の再計測の契機。
  const [layoutSignal, setLayoutSignal] = useState(0);
  // 観測中の図形本体 → 購読解除関数。同じ本体を複数のキャレットが指しても購読は 1 つで足りる。
  const subscriptionsRef = useRef<Map<HTMLElement, () => void>>(new Map());
  const handleObjectLayoutChange = useCallback(
    () => setLayoutSignal((value) => value + 1),
    []
  );
  // 今回キャレットを描いた図形の本体だけを観測対象にする（不要になった本体は購読を解く）。
  const syncObjectSubscriptions = useCallback(
    (roots: readonly HTMLElement[]): void => {
      const subscriptions = subscriptionsRef.current;
      const wanted = new Set(roots);
      for (const [element, unobserve] of subscriptions) {
        if (wanted.has(element)) continue;
        unobserve();
        subscriptions.delete(element);
      }
      for (const element of wanted) {
        if (subscriptions.has(element)) continue;
        subscriptions.set(element, observeObjectLayout(element, handleObjectLayoutChange));
      }
    },
    [handleObjectLayoutChange]
  );

  // アンマウント時に観測を残さない（レイヤが消えた後に通知が来ても意味がない）。
  useEffect(() => {
    const subscriptions = subscriptionsRef.current;
    return () => {
      for (const unobserve of subscriptions.values()) unobserve();
      subscriptions.clear();
    };
  }, []);

  useEffect(() => {
    const onScroll = (event: Event): void => {
      const target = event.target;
      if (!(target instanceof Element) || !target.classList.contains("canvas-table-object")) return;
      setTableScrollSignal((value) => value + 1);
    };
    // スクロールイベントは bubbling しないため、capture で拾う。
    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, []);

  // 編集停止後に残るキャレットを定期的に掃除する（プレビューゴーストと同じ間隔）。
  useEffect(() => {
    const id = window.setInterval(
      () => prune(REMOTE_TRANSIENT_STALE_MS),
      REMOTE_TRANSIENT_PRUNE_INTERVAL_MS
    );
    return () => window.clearInterval(id);
  }, [prune]);

  // textCarets / objects / viewers の変化時にエディタ DOM を実測して位置を求める。
  // pan / zoom では位置（ステージ座標）は不変のため、view は依存に含めず getState() で都度読む。
  useLayoutEffect(() => {
    const measure = (): Record<string, CaretPosition> => {
      const result = measureAllCarets({
        textCarets,
        objects,
        viewers,
        currentUserId,
        view: useCanvasStore.getState().view,
      });
      syncObjectSubscriptions(result.roots);
      return result.positions;
    };
    // 依存に positions を含めないため、この setState でこの effect は再実行されない（ループしない）。
    setPositions(measure());
    // 付箋の文字サイズ再計算（setFittedFontSize による同期再レンダー）は、すべての layout effect の
    // 後に走る。同じフレームの描画前にもう一度測り直し、旧文字サイズのレイアウトで得た位置を残さない。
    let handle: number | null = null;
    if (typeof window.requestAnimationFrame === "function") {
      handle = window.requestAnimationFrame(() => {
        const again = measure();
        setPositions((previous) => (samePositions(previous, again) ? previous : again));
      });
    }
    return () => {
      if (handle !== null) window.cancelAnimationFrame(handle);
    };
  }, [
    textCarets,
    objects,
    viewers,
    currentUserId,
    tableScrollSignal,
    layoutSignal,
    syncObjectSubscriptions,
  ]);

  return (
    <div
      data-testid="canvas-remote-carets-layer"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: CARET_LAYER_Z_INDEX,
      }}
    >
      {Object.entries(positions).map(([key, pos]) => (
        <Fragment key={key}>
          {/* 帯はキャレットより先に置く（同じ z-index では先に書いたものが背面になる）。 */}
          {pos.selectionRects.map((rect, index) => (
            <div
              key={`${key}:sel:${index}`}
              data-testid="remote-text-selection"
              style={{
                position: "absolute",
                left: `${rect.stageX}px`,
                top: `${rect.stageY}px`,
                width: `${rect.stageWidth}px`,
                height: `${rect.stageHeight}px`,
                // 下の本文が読めるだけの薄さにする。色は選択者の色。
                background: `color-mix(in srgb, ${pos.color} 28%, transparent)`,
                borderRadius: "calc(2px * var(--canvas-inv-zoom, 1))",
                pointerEvents: "none",
              }}
            />
          ))}
          <div
            data-testid="remote-text-caret"
            style={{
              position: "absolute",
              left: `${pos.stageX}px`,
              top: `${pos.stageY}px`,
              height: `${pos.stageHeight}px`,
              // バー幅はズーム非依存で常に約 2px に見せる（選択 UI と同じ補正）。
              // 枠へ切り詰めた経路では、枠の右辺までの残り幅を上限に重ねる（--canvas-inv-zoom は
              // store の倍率で描かれるため、DOM 実測の倍率と食い違う瞬間に右辺を超え得る）。
              width:
                pos.barMaxWidth === undefined
                  ? "calc(2px * var(--canvas-inv-zoom, 1))"
                  : `min(calc(2px * var(--canvas-inv-zoom, 1)), ${pos.barMaxWidth}px)`,
              background: pos.color,
              pointerEvents: "none",
            }}
          >
            {pos.displayName ? (
              <div
                data-testid="remote-text-caret-label"
                style={{
                  position: "absolute",
                  left: 0,
                  bottom: "100%",
                  // ラベルもズーム非依存サイズにする。
                  // 縦バーとの隙間も scale の右側の translateY で表し、画面上の px として効かせる
                  // （margin は transform の外側で効くため、隙間だけが拡大率に比例してしまう）。
                  transform: `scale(var(--canvas-inv-zoom, 1)) translateY(${-REMOTE_CARET_LABEL_GAP_PX}px)`,
                  transformOrigin: "left bottom",
                  padding: "1px 6px",
                  background: pos.color,
                  color: "#ffffff",
                  fontSize: "11px",
                  fontWeight: 700,
                  borderRadius: "4px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                }}
              >
                {pos.displayName}
              </div>
            ) : null}
          </div>
        </Fragment>
      ))}
    </div>
  );
}

/** 1 回の計測で得た描画位置と、レイアウト変化の観測対象になる図形本体。 */
interface MeasureResult {
  readonly positions: Record<string, CaretPosition>;
  readonly roots: readonly HTMLElement[];
}

/**
 * すべてのリモートキャレットを 1 回分まとめて実測する（layout effect と rAF 追い計測が共用する）。
 *
 * 座標は「図形本体の DOM 矩形を原点とする相対値」で求め、原点には store の object.x / y を使う。
 * 他参加者の選択枠も object.x / y を原点に描かれるため、本体の DOM と store が一時的に食い違っても、
 * キャレットは枠に対して正しい相対位置に出る。
 */
function measureAllCarets(params: {
  readonly textCarets: Record<string, RemoteTextCaret>;
  readonly objects: Record<string, CanvasObject>;
  readonly viewers: Record<string, Viewer>;
  readonly currentUserId: string;
  readonly view: ViewState;
}): MeasureResult {
  const { textCarets, objects, viewers, currentUserId, view } = params;
  // ステージ要素は width: CANVAS_WIDTH_PX に scale(zoom) を掛けているため、実測幅の比が DOM 上の倍率。
  const stageEl = document.querySelector<HTMLElement>('[data-testid="canvas-stage"]');
  const stageZoom = resolveStageZoom(stageEl?.getBoundingClientRect().width, view.zoom);
  const now = Date.now();
  const positions: Record<string, CaretPosition> = {};
  const roots: HTMLElement[] = [];
  for (const [key, caret] of Object.entries(textCarets)) {
    if (caret.userId === currentUserId) continue;
    if (now - caret.updatedAt > REMOTE_TRANSIENT_STALE_MS) continue;
    const object = objects[caret.objectId];
    if (!object) continue;
    // 回転オブジェクトはキャレット位置が不正確になるため描画しない。
    if (object.rotation) continue;
    if (object.type === "table") {
      const position = measureTableCellCaret(caret, object, view, viewers);
      if (position) positions[key] = position;
      continue;
    }
    if (object.type !== "text" && object.type !== "sticky") continue;
    // ID は引用符・"]"・空白を含み得るため、セレクタへ補間する前にエスケープする。
    const el = document.querySelector<HTMLElement>(
      `[data-testid="canvas-object-text-${CSS.escape(caret.objectId)}"]`
    );
    if (!el) continue;
    const root = findCanvasObjectElement<HTMLElement>(caret.objectId);
    if (root) roots.push(root);
    const anchor = resolveObjectAnchor(object, root, stageZoom, view);
    // 生オフセット（cursorIndex）を、表示 DOM の「行 index + 行内オフセット」へ写像する。
    // surface: 編集中 WYSIWYG DOM（[data-md] ブロックあり）は "editor"、非編集表示は "view"。
    const surface: CaretSurface = el.querySelector("[data-md]") ? "editor" : "view";
    const display = mapRawOffsetToDisplay(object.text, caret.cursorIndex, surface);
    // 対象行（表ではセル）の要素内だけを走査する。要素が取れない場合は従来どおり全体を走査する（フォールバック）。
    const lineEl = findDisplayLineElement(el, display.lineIndex, display.cellIndex);
    const target = lineEl ?? el;
    const offset = lineEl !== null ? display.lineOffset : caret.cursorIndex;
    const rect = measureCaretClientRect(target, offset, anchor.zoom);
    if (!rect) continue;
    // 行要素が取れているときだけ、その行の本文が始まる位置を下限にする。ぶら下げインデントのある行
    // （箇条書き・番号付きリスト）では、行要素の左端はマーカー領域の左端であり本文の開始位置ではない。
    // 帯（measureSelectionLocalRects）は行の実描画範囲をそのまま写すため、この下限の対象にしない。
    const caretLeft =
      lineEl === null
        ? rect.left
        : clampCaretLeftToLineContent(
            rect.left,
            lineEl.getBoundingClientRect(),
            Number.parseFloat(getComputedStyle(lineEl).paddingLeft),
            anchor.zoom
          );
    const local = anchor.toLocal({ left: caretLeft, top: rect.top, width: 0, height: rect.height });
    const clamped = clampCaretToBox(
      { x: local.x, y: local.y, height: local.height },
      anchor.box,
      REMOTE_CARET_BAR_SCREEN_PX / anchor.zoom
    );
    if (!clamped) continue;
    const viewer = viewers[caret.userId];
    // 帯はキャレットを求めた後に独立して算出する。ここが失敗しても空配列になるだけで、
    // 既存のキャレット表示（縦バーと名前ラベル）は必ず描かれる。
    const selectionRects =
      caret.selectionEndIndex !== undefined
        ? measureSelectionLocalRects(
            el,
            object.text,
            surface,
            caret.cursorIndex,
            caret.selectionEndIndex,
            anchor
          )
            .map((rectLocal) => clipRectToBox(rectLocal, anchor.box))
            .filter((rectLocal): rectLocal is ObjectLocalRect => rectLocal !== null)
            .map((rectLocal) => ({
              stageX: object.x + rectLocal.x,
              stageY: object.y + rectLocal.y,
              stageWidth: rectLocal.width,
              stageHeight: rectLocal.height,
            }))
        : [];
    positions[key] = {
      stageX: object.x + clamped.x,
      stageY: object.y + clamped.y,
      stageHeight: clamped.height,
      barMaxWidth: anchor.box.width - clamped.x,
      color: normalizeColor(viewer?.color),
      displayName: viewer?.displayName ?? "",
      selectionRects,
    };
  }
  return { positions, roots };
}

/**
 * 図形本体を原点とする相対座標への変換器を作る。
 * 本体要素が無い、または矩形の幅と高さがともに 0 で原点を取れない場合は、従来どおり
 * screenToStage で変換したうえで store の原点（object.x / y）からの相対値にする。
 */
function resolveObjectAnchor(
  object: CanvasObject,
  root: HTMLElement | null,
  stageZoom: number,
  view: ViewState
): ObjectAnchor {
  const rootRect = root?.getBoundingClientRect();
  if (root && rootRect && (rootRect.width > 0 || rootRect.height > 0)) {
    // 枠の高さは選択枠側（useRenderedObjectHeight）と同じ offsetHeight を使い、両レイヤで一致させる。
    const box = resolveClampBox(object, root.offsetHeight);
    return {
      zoom: stageZoom,
      box,
      toLocal: (rect) => toObjectLocalRect(rect, rootRect, stageZoom),
    };
  }
  const box = resolveClampBox(object, null);
  return {
    zoom: view.zoom,
    box,
    toLocal: (rect) => {
      const stage = screenToStage({ x: rect.left, y: rect.top }, view);
      return {
        x: stage.x - object.x,
        y: stage.y - object.y,
        width: rect.width / view.zoom,
        height: rect.height / view.zoom,
      };
    },
  };
}

/** rAF 追い計測の結果が前回と同じなら再レンダーしないための同値判定（数値は完全一致で比べる）。 */
function samePositions(
  left: Record<string, CaretPosition>,
  right: Record<string, CaretPosition>
): boolean {
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  for (const key of leftKeys) {
    const a = left[key];
    const b = right[key];
    if (!a || !b) return false;
    if (a.stageX !== b.stageX || a.stageY !== b.stageY || a.stageHeight !== b.stageHeight) {
      return false;
    }
    if (a.barMaxWidth !== b.barMaxWidth) return false;
    if (a.color !== b.color || a.displayName !== b.displayName) return false;
    if (a.selectionRects.length !== b.selectionRects.length) return false;
    for (let index = 0; index < a.selectionRects.length; index += 1) {
      const rectA = a.selectionRects[index];
      const rectB = b.selectionRects[index];
      if (!rectA || !rectB) return false;
      if (
        rectA.stageX !== rectB.stageX ||
        rectA.stageY !== rectB.stageY ||
        rectA.stageWidth !== rectB.stageWidth ||
        rectA.stageHeight !== rectB.stageHeight
      ) {
        return false;
      }
    }
  }
  return true;
}

/**
 * 表のセル内のキャレット位置を実測する。
 *
 * 受信 op の objectId に対応する表オブジェクトの root を先に特定し、**その root の子孫だけ**から
 * 対象セルを引く。document 全体でセルを検索すると、配置プレビューや別の表の同じ行列位置を
 * 拾ってしまう。行・列が消えた直後などで要素が見つからない場合は、例外を投げずに描かない
 * （次の更新で自然に消える）。
 */
function measureTableCellCaret(
  caret: RemoteTextCaret,
  object: TableCanvasObject,
  view: ViewState,
  viewers: Record<string, Viewer>
): CaretPosition | null {
  const cell = caret.tableCell;
  if (!cell) return null;
  // 送信時点と形が違う表では、同じ行列位置が別のセルを指す。描かずに次の更新を待つ。
  if (tableStructureRevisionOf(object.structureRevision) !== cell.structureRevision) return null;
  const source = object.cells[cell.row]?.[cell.column];
  if (source === undefined) return null;
  const root = document.querySelector<HTMLElement>(
    `[data-testid="canvas-object-${CSS.escape(caret.objectId)}"][data-object-type="table"]`
  );
  if (!root) return null;
  const cellEl = root.querySelector<HTMLElement>(
    `[data-table-cell="${CSS.escape(`${cell.row}-${cell.column}`)}"]`
  );
  if (!cellEl) return null;
  // 送信側は入力欄の生テキスト上の位置を送るが、編集していない側のセルは記法を解釈した
  // 表示テキストで描かれる（`**高**` → `高`）。生オフセットのまま当てると記法の文字数だけずれるため、
  // 本文の行と同じ規則で表示オフセットへ写像してから実測する。
  const rect = measureCaretClientRect(
    cellEl,
    tableObjectCellDisplayOffset(source, caret.cursorIndex),
    view.zoom
  );
  if (!rect) return null;
  // セルの clip（表の外枠と行の切り取り）と交差しない位置は描かない。
  const clip = cellEl.getBoundingClientRect();
  const rootRect = root.getBoundingClientRect();
  if (clip.width > 0 && clip.height > 0) {
    if (rect.left < clip.left - 1 || rect.left > clip.right + 1) return null;
    if (rect.top + rect.height < clip.top - 1 || rect.top > clip.bottom + 1) return null;
  }
  if (rootRect.width > 0 && rootRect.height > 0) {
    if (rect.left < rootRect.left - 1 || rect.left > rootRect.right + 1) return null;
    if (rect.top + rect.height < rootRect.top - 1 || rect.top > rootRect.bottom + 1) return null;
  }
  const stage = screenToStage({ x: rect.left, y: rect.top }, view);
  const viewer = viewers[caret.userId];
  return {
    stageX: stage.x,
    stageY: stage.y,
    stageHeight: rect.height / view.zoom,
    color: normalizeColor(viewer?.color),
    displayName: viewer?.displayName ?? "",
    // セル編集では選択範囲を共有しない（1 セルは短く、範囲表示の価値が低いため）。
    selectionRects: [],
  };
}

/**
 * エディタ要素 el 内で、本文先頭から cursorIndex 文字目のキャレット位置（client 座標）を実測する。
 * テキストノードを走査して cursorIndex に対応する位置へ collapsed Range を作り、その矩形を返す。
 *
 * 戻り値の height は **画面 px**（呼び出し側が倍率で割ってステージ単位へ戻す）。空行フォールバックで
 * 使う line-height は CSS px（ステージ単位）のため、zoom を掛けてから返す（等倍以外での単位ずれ防止）。
 */
function measureCaretClientRect(
  el: HTMLElement,
  cursorIndex: number,
  zoom: number
): { left: number; top: number; height: number } | null {
  const range = document.createRange();
  // 本文テキストノード上の位置を解決する。編集中箇条書きの編集不可マーカー span（`• `）は
  // 表示専用で本文オフセットに含めないため resolveCaretTextAnchor 側で除外される。
  const anchor = resolveCaretTextAnchor(el, cursorIndex);
  if (!anchor) {
    // テキストノードなし（空）→ 要素先頭。
    range.selectNodeContents(el);
    range.collapse(true);
  } else {
    range.setStart(anchor.node, anchor.offset);
    range.collapse(true);
  }
  const rect = range.getBoundingClientRect();
  if (rect && rect.height > 0) {
    return { left: rect.left, top: rect.top, height: rect.height };
  }
  // collapsed Range の矩形が取れない環境向けフォールバック（要素の rect と line-height で概算）。
  const elRect = el.getBoundingClientRect();
  if (elRect.width === 0 && elRect.height === 0) return null;
  const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
  const scale = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const height =
    Number.isFinite(lineHeight) && lineHeight > 0
      ? lineHeight * scale
      : elRect.height || 16 * scale;
  return {
    left: rect?.left && rect.left > 0 ? rect.left : elRect.left,
    top: rect?.top && rect.top > 0 ? rect.top : elRect.top,
    height,
  };
}

/**
 * エディタ要素 el 内の生オフセット [startIndex, endIndex) に対応する選択帯を、
 * 行ボックスごとの「図形本体を原点とする相対矩形」として返す（切り詰めは呼び出し側が行う）。
 *
 * 行の折り返し位置・比例フォントの字幅・Markdown 整形後の字下げはすべて DOM の
 * レイアウト結果であり、再現計算するとずれるため、Range.getClientRects() に行分割を任せる。
 * 複数行をまたぐ単一 Range は使わない。CSSOM View の規定により getClientRects() は
 * 「範囲に完全に含まれる要素の border box」も返すため、中間行の <div>（行のコンテナ幅
 * いっぱい）が矩形として混ざり、文字の無い行末余白まで帯が伸びてしまう。代わりに
 * 行ごとに「その行の内側だけに収まる Range」を張り、行要素自身が完全には含まれないようにする。
 * ただし行の内側にあるインライン要素（`<span>` / `<a>`）の box は返るため、行ボックスと
 * 重なる矩形は dropContainedRects で 1 枚へまとめる（重ねて描くと帯が濃くなるため）。
 *
 * 境界の解決はキャレット 1 点の解決とまったく同じ経路（mapRawOffsetToDisplay →
 * resolveCaretTextAnchor）を 2 回使う。解決できない場合は空配列または一部欠落で返し、
 * 呼び出し側はキャレットだけを描く。
 */
function measureSelectionLocalRects(
  el: HTMLElement,
  text: string,
  surface: CaretSurface,
  startIndex: number,
  endIndex: number,
  anchor: ObjectAnchor
): readonly ObjectLocalRect[] {
  if (endIndex <= startIndex) return [];
  try {
    const from = mapRawOffsetToDisplay(text, startIndex, surface);
    const to = mapRawOffsetToDisplay(text, endIndex, surface);
    // 表示写像後に同一点・逆順へ潰れる範囲（記法部分だけの選択など）は帯を描かない。
    // 表の行では `lineOffset` がセル内の相対位置になるため、行だけでなくセルの順序も含めて比べる
    // （同じ行の別セルへまたがる選択を「逆順」と誤判定しないため）。
    if (comparePositions(to, from) <= 0) return [];
    const result: ObjectLocalRect[] = [];
    const lastLine = Math.min(to.lineIndex, from.lineIndex + REMOTE_SELECTION_MAX_LINES - 1);
    for (let lineIndex = from.lineIndex; lineIndex <= lastLine; lineIndex += 1) {
      if (result.length >= REMOTE_SELECTION_MAX_RECTS) break;
      // 非編集表示の表の行は、1 つの要素ではなく複数のセル要素へ分かれる。単一の Range では
      // 表せないため、選択範囲に重なるセル要素ごとに Range を作り、それぞれの矩形を集める
      // （既存の「1 行から複数矩形を集める」経路をセル単位で使う）。
      const cellEls = findDisplayCellElements(el, lineIndex);
      if (cellEls.length > 0) {
        const firstCell = lineIndex === from.lineIndex ? (from.cellIndex ?? 0) : 0;
        const lastCell =
          lineIndex === to.lineIndex ? (to.cellIndex ?? cellEls.length - 1) : cellEls.length - 1;
        for (let cellIndex = firstCell; cellIndex <= lastCell; cellIndex += 1) {
          if (result.length >= REMOTE_SELECTION_MAX_RECTS) break;
          const cellEl = cellEls[cellIndex];
          if (cellEl === undefined) continue;
          const isFromCell = lineIndex === from.lineIndex && cellIndex === from.cellIndex;
          const isToCell = lineIndex === to.lineIndex && cellIndex === to.cellIndex;
          // 終端のセルで 0 文字目が終端なら、そのセルは 1 文字も選択されていない。
          if (isToCell && to.lineOffset === 0) continue;
          const painted = collectSelectionRects(
            cellEl,
            isFromCell ? from.lineOffset : undefined,
            isToCell ? to.lineOffset : undefined,
            REMOTE_SELECTION_MAX_RECTS - result.length
          );
          if (painted === null) return [];
          for (const rect of painted) result.push(anchor.toLocal(rect));
        }
        continue;
      }
      // 半開区間の終端が次行の先頭にあるとき、その行は 1 文字も選択されていない。
      // 行要素の先頭と最初のテキストノードの offset 0 は DOM Range 上では別境界になり、
      // range.collapsed だけでは弾けないため、ここで明示的に除外する。
      if (lineIndex === to.lineIndex && to.lineOffset === 0) continue;
      const lineEl = findDisplayLineElement(el, lineIndex, undefined);
      if (lineEl === null) continue;
      const painted = collectSelectionRects(
        lineEl,
        lineIndex === from.lineIndex ? from.lineOffset : undefined,
        lineIndex === to.lineIndex ? to.lineOffset : undefined,
        REMOTE_SELECTION_MAX_RECTS - result.length
      );
      if (painted === null) return [];
      for (const rect of painted) result.push(anchor.toLocal(rect));
      const pushedForLine = painted.length;
      // 空行（<br> だけの行）は矩形が返らないか幅 0 になる。選択が途切れて見えないよう
      // 細い帯を置く。ただし幅 0 のキャレット矩形や隠れた記法を空行と誤認しないよう、
      // 本文テキストノードが 1 つも無い行だけを対象にする。
      if (
        pushedForLine === 0 &&
        !resolveCaretTextAnchor(lineEl, 0) &&
        result.length < REMOTE_SELECTION_MAX_RECTS
      ) {
        const rect = lineEl.getBoundingClientRect();
        if (rect.height > 0) {
          result.push(
            anchor.toLocal({
              left: rect.left,
              top: rect.top,
              height: rect.height,
              width: EMPTY_LINE_BAND_WIDTH_PX,
            })
          );
        }
      }
    }
    return result;
  } catch {
    return [];
  }
}

/**
 * 表示 DOM 上の 2 点を文書順で比較する（負: a が前、0: 同じ、正: a が後ろ）。
 * 行 → セル（表の外は 0 とみなす）→ 行内 / セル内オフセットの辞書順。
 */
function comparePositions(a: DisplayCaretPosition, b: DisplayCaretPosition): number {
  if (a.lineIndex !== b.lineIndex) return a.lineIndex - b.lineIndex;
  const aCell = a.cellIndex ?? 0;
  const bCell = b.cellIndex ?? 0;
  if (aCell !== bCell) return aCell - bCell;
  return a.lineOffset - b.lineOffset;
}

/**
 * 表示 DOM から、生 Markdown の `lineIndex` 行に対応する要素を引く。
 * 表以外の行は行の `<div>`、非編集表示の表の行は `cellIndex` の列のセル要素（th / td）。
 * 行の識別は子要素の index ではなく `data-md-line` 属性で行う（表の行はセル要素だけが持ち、
 * wrapper・table・tr には付かない）。該当要素が無ければ null。
 */
function findDisplayLineElement(
  el: HTMLElement,
  lineIndex: number,
  cellIndex: number | undefined
): HTMLElement | null {
  const selector =
    cellIndex === undefined
      ? `[data-md-line="${lineIndex}"]`
      : `[data-md-line="${lineIndex}"][data-md-cell="${cellIndex}"]`;
  const found = el.querySelector(selector);
  return found instanceof HTMLElement ? found : null;
}

/** 非編集表示の表の行に属するセル要素を列順（= 文書順）で返す。表の行でなければ空配列。 */
function findDisplayCellElements(el: HTMLElement, lineIndex: number): readonly HTMLElement[] {
  return Array.from(el.querySelectorAll(`[data-md-line="${lineIndex}"][data-md-cell]`)).filter(
    (node): node is HTMLElement => node instanceof HTMLElement
  );
}

/**
 * 1 つの要素（行または表のセル）の内側だけに収まる Range を張り、選択帯の矩形（client 座標）を集める。
 * `startOffset` / `endOffset` は要素の表示テキスト上のオフセットで、undefined なら要素の先頭 / 末尾。
 * 矩形を 1 つも持たない（collapsed）場合は空配列、Range がレイアウト系メソッドを持たない環境では
 * null（呼び出し側は帯を描かずキャレットだけを描く）。
 */
function collectSelectionRects(
  target: HTMLElement,
  startOffset: number | undefined,
  endOffset: number | undefined,
  limit: number
): readonly ScreenRect[] | null {
  const range = target.ownerDocument.createRange();
  range.selectNodeContents(target);
  if (startOffset !== undefined) {
    const anchor = resolveCaretTextAnchor(target, startOffset);
    if (anchor) range.setStart(anchor.node, anchor.offset);
  }
  if (endOffset !== undefined) {
    const anchor = resolveCaretTextAnchor(target, endOffset);
    if (anchor) range.setEnd(anchor.node, anchor.offset);
  }
  if (range.collapsed) return [];
  // jsdom の Range はレイアウト系メソッドを持たない。存在確認は防御ではなく必須。
  if (typeof range.getClientRects !== "function") return null;
  const rects = range.getClientRects();
  const lineRects: ScreenRect[] = [];
  for (let i = 0; i < rects.length && lineRects.length < REMOTE_SELECTION_MAX_LINE_RECTS; i += 1) {
    const rect = rects[i];
    if (!rect || rect.width <= 0 || rect.height <= 0) continue;
    lineRects.push({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }
  // 重複除去を先に行ってから描画上限を適用する。逆順にすると、重なった矩形が
  // 描画枠を食い潰し、実際に描ける行数・矩形数が上限より減ってしまう。
  return dropContainedRects(lineRects).slice(0, Math.max(0, limit));
}

/** 矩形の包含判定に使う許容差（px）。行ボックスとインライン要素の box は端が微小にずれる。 */
const RECT_CONTAINMENT_EPSILON_PX = 0.5;

/**
 * 同じ行の矩形のうち、他の矩形へ収まるものを取り除く。
 *
 * getClientRects() は行ボックスに加えて、範囲へ完全に含まれるインライン要素
 * （表示 DOM の `<span>` / `<a>`）の border box も返す。とくに中間行は行の内容全体を
 * 範囲にするため、行ボックスと同じ位置の box が二重に返る。そのまま描くと半透明の帯が
 * 重なってその行だけ濃くなるため、面積の大きい方だけを残す。
 */
function dropContainedRects(rects: readonly ScreenRect[]): readonly ScreenRect[] {
  // 面積の大きい順に採否を決める（大きい矩形が残り、そこへ収まる小さい矩形が落ちる）。
  const byAreaDesc = rects
    .map((rect, index) => ({ index, rect }))
    .sort((a, b) => b.rect.width * b.rect.height - a.rect.width * a.rect.height);
  const keptIndexes = new Set<number>();
  const kept: ScreenRect[] = [];
  for (const entry of byAreaDesc) {
    if (kept.some((other) => containsRect(other, entry.rect))) continue;
    kept.push(entry.rect);
    keptIndexes.add(entry.index);
  }
  // 返すときは元の並び（行ボックスの上から下へ）に戻す。上限で切り詰めたときに、
  // 面積の大きい矩形ではなく上の行から順に残るようにするため。
  return rects.filter((_rect, index) => keptIndexes.has(index));
}

function containsRect(outer: ScreenRect, inner: ScreenRect): boolean {
  return (
    inner.left >= outer.left - RECT_CONTAINMENT_EPSILON_PX &&
    inner.top >= outer.top - RECT_CONTAINMENT_EPSILON_PX &&
    inner.left + inner.width <= outer.left + outer.width + RECT_CONTAINMENT_EPSILON_PX &&
    inner.top + inner.height <= outer.top + outer.height + RECT_CONTAINMENT_EPSILON_PX
  );
}

function normalizeColor(color: string | undefined): string {
  return color && color.trim().length > 0 ? color : "#1e63d4";
}
